export const CHUNK_RELOAD_AT_KEY = "chunkLoadErrorReloadAt";
export const CHUNK_RELOAD_WINDOW_MS = 60000;

const CHUNK_LOAD_ERROR_PATTERN =
  /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed|Unexpected token ['"]?</i;

const isJavaScriptAsset = (filename = "") => /\.m?js(?:[?#].*)?$/i.test(filename);

export const getErrorMessage = (errorLike) => {
  if (!errorLike) {
    return "";
  }

  if (typeof errorLike === "string") {
    return errorLike;
  }

  if (typeof errorLike.message === "string") {
    return errorLike.message;
  }

  try {
    return String(errorLike);
  } catch (_) {
    return "";
  }
};

export const isChunkLoadError = ({ message = "", filename = "", error = null } = {}) => {
  const normalizedMessage = getErrorMessage(message) || getErrorMessage(error);
  const normalizedFilename = filename || error?.fileName || error?.filename || "";

  return (
    error?.name === "ChunkLoadError" ||
    CHUNK_LOAD_ERROR_PATTERN.test(normalizedMessage) ||
    (isJavaScriptAsset(normalizedFilename) && /Unexpected token/i.test(normalizedMessage))
  );
};

let reloadPending = false;

const readLastReloadAt = () => {
  try {
    return Number(sessionStorage.getItem(CHUNK_RELOAD_AT_KEY)) || 0;
  } catch (_) {
    return 0;
  }
};

/**
 * Reloads the page once per CHUNK_RELOAD_WINDOW_MS so a tab holding a stale bundle picks up the
 * new build. Returns true when a reload was triggered, false when the guard blocked it (the
 * caller must then show an error/retry state instead of an empty page).
 */
export const tryGuardedChunkReload = ({ source = "", message = "", filename = "" } = {}) => {
  if (reloadPending) {
    return true;
  }

  const now = Date.now();

  if (now - readLastReloadAt() < CHUNK_RELOAD_WINDOW_MS) {
    window.Sentry?.captureException(new Error(`Chunk loading failed: ${message}`), {
      tags: { chunk_error: true },
      extra: { source, filename },
    });
    return false;
  }

  try {
    sessionStorage.setItem(CHUNK_RELOAD_AT_KEY, String(now));
  } catch (_) {
    return false;
  }

  reloadPending = true;
  window.location.reload();
  return true;
};

/** Handles any caught error: guarded reload if it is a chunk error. */
export const recoverFromChunkError = (error, source) => {
  const message = getErrorMessage(error);
  const filename = error?.fileName || error?.filename || "";

  if (!isChunkLoadError({ message, filename, error })) {
    return false;
  }

  return tryGuardedChunkReload({ source, message, filename });
};

export const resetChunkReloadState = () => {
  reloadPending = false;
};
